import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Masonry } from '@mui/lab'
import { FormControlLabel, Checkbox, Box, Card, CardContent, Divider, Tooltip } from '@mui/material'

import { Tag } from '../../../../api/core'
import { AllOrNothingSelector } from '../../../../components/AllOrNothingSelector/AllOrNothingSelector'
import { GRAccordion } from '../../../../components/GRAccordion/GRAccordion'
import { useLiveEventsSecondaryTagGroups } from '../../hooks/liveEventsSecondaryTaxonomy'
import { LiveEventsTag } from '../../types/LiveEventsTag'
import './LiveEventsSecondaryTaxonomySelector.scss'

interface LiveEventsSecondaryTaxonomySelectorProps {
  selectableLiveEventSecondaryTags: LiveEventsTag[]
  selectedLiveEventSecondaryTags: Tag[]
  /** When provided, tags with id mapping to false are shown disabled with tooltip */
  applicability?: Record<string, boolean>
  isLoading?: boolean
  open?: boolean
  onLiveEventsSecondaryTaxonomySelectChange: (selectedTags: Tag[], selection: { id: string; name: string }) => void
  onLiveEventsSecondaryTaxonomyCleared: () => void
  onLiveEventsSecondaryTaxonomySelectAll: () => void
}

const LiveEventsSecondaryTaxonomySelector: React.FC<LiveEventsSecondaryTaxonomySelectorProps> = ({
  selectableLiveEventSecondaryTags,
  selectedLiveEventSecondaryTags,
  applicability,
  open,
  isLoading,
  onLiveEventsSecondaryTaxonomySelectChange,
  onLiveEventsSecondaryTaxonomyCleared,
  onLiveEventsSecondaryTaxonomySelectAll,
}) => {
  const { t } = useTranslation()
  const [liveEventsSecondaryTaxonomyOpen, setLiveEventsSecondaryTaxonomyOpen] = useState(open || false)
  const [selectedTags, setSelectedTags] = useState<LiveEventsTag[]>(selectedLiveEventSecondaryTags)
  const { data: liveEventSecondaryTagGroups } = useLiveEventsSecondaryTagGroups()
  const notApplicableTitle = t('live-events:filter_option_not_applicable_tooltip')

  const selectableLiveEventSecondaryTagGroups = useMemo(() => {
    return (
      liveEventSecondaryTagGroups
        // Filter out groups that have no selectable tags
        ?.filter((group) => {
          return group.tags.some((tagId) => {
            return selectableLiveEventSecondaryTags?.some((tag) => tag.id === tagId)
          })
        })
        .map((group) => {
          return {
            ...group,
            tags: group.tags
              // Filter out tags that are not selectable
              .filter((tagId) => {
                return selectableLiveEventSecondaryTags?.some((tag) => tag.id === tagId)
              })
              // set checked state for tags
              .map((tagId) => {
                const tag = selectableLiveEventSecondaryTags?.find((tag) => tag.id === tagId)
                return {
                  ...tag,
                  checked: selectedTags.some((selectedTag) => selectedTag.id === tagId),
                }
              }),
          }
        })
        // Set checked and indeterminate state for groups (only applicable tags count when applicability is set)
        .map((group) => {
          const applicableTagsInGroup = applicability ? group.tags.filter((tag) => applicability[tag.id ?? ''] !== false) : group.tags
          const applicableCheckedCount = applicableTagsInGroup.filter((tag) => tag.checked).length
          return {
            ...group,
            checked: applicableTagsInGroup.length > 0 && applicableCheckedCount === applicableTagsInGroup.length,
            indeterminate: applicableCheckedCount > 0 && applicableCheckedCount < applicableTagsInGroup.length,
          }
        })
        // sort groups by order
        .sort((a, b) => a.order - b.order)
    )
  }, [liveEventSecondaryTagGroups, selectableLiveEventSecondaryTags, selectedTags, applicability])

  // Handle tag selection logic here (no-op when tag is not applicable)
  const handleSelectTag = useCallback(
    (groupId: string, tagId: string) => {
      const group = selectableLiveEventSecondaryTagGroups?.find((group) => group.id === groupId)
      const tag = selectableLiveEventSecondaryTags?.find((tag) => tag.id === tagId)

      if (!group || !tag) return () => {}

      const isTagDisabled = applicability ? applicability[tag.id] === false : false
      if (isTagDisabled) return () => {}

      return (event: React.ChangeEvent<HTMLInputElement>) => {
        const isChecked = event.target.checked

        // Create a new array to represent the updated state
        const updatedTags = [...selectedTags]

        if (isChecked) {
          updatedTags.push(tag)
        } else {
          const index = updatedTags.findIndex((tag) => tag.id === tagId)
          updatedTags.splice(index, 1)
        }

        onLiveEventsSecondaryTaxonomySelectChange(updatedTags as Tag[], tag as Tag)

        // Update the state with the new array
        setSelectedTags(updatedTags)
      }
    },
    [applicability, onLiveEventsSecondaryTaxonomySelectChange, selectableLiveEventSecondaryTagGroups, selectableLiveEventSecondaryTags, selectedTags]
  )

  // Handle group selection logic here
  const handleSelectGroup = useCallback(
    (groupId: string) => {
      return (event: React.ChangeEvent<HTMLInputElement>) => {
        const isChecked = event.target.checked

        // Create a new array to represent the updated state
        const updatedTags = [...selectedTags]

        const group = liveEventSecondaryTagGroups?.find((group) => group.id === groupId)

        if (!group) return

        const changedTags: LiveEventsTag[] = []

        if (isChecked) {
          // Add only applicable tags from the group
          group.tags.forEach((tagId) => {
            const tag = selectableLiveEventSecondaryTags?.find((tag) => tagId === tag.id)
            const isApplicable = !applicability || applicability[tag?.id ?? ''] !== false
            if (tag && isApplicable && !updatedTags.some((updatedTag) => updatedTag.id === tag.id)) {
              updatedTags.push(tag)
              changedTags.push(tag)
            }
          })
        } else {
          // Remove all tags from the group from the updated state
          group.tags.forEach((tagId) => {
            const index = updatedTags.findIndex((tag) => tagId === tag.id)

            if (index === -1) return // do nothing if the tag is not in the array

            updatedTags.splice(index, 1)

            const tag = selectableLiveEventSecondaryTags?.find((tag) => tagId === tag.id)
            if (tag) {
              changedTags.push(tag)
            }
          })
        }

        // Loop through chanhed tags and call onLiveEventsSecondaryTaxonomySelectChange for each
        changedTags.forEach((tag) => {
          onLiveEventsSecondaryTaxonomySelectChange(updatedTags as Tag[], tag as Tag)
        })

        // Update the state with the new array
        setSelectedTags(updatedTags)
      }
    },
    [applicability, liveEventSecondaryTagGroups, onLiveEventsSecondaryTaxonomySelectChange, selectableLiveEventSecondaryTags, selectedTags]
  )

  useEffect(() => {
    setSelectedTags(selectedLiveEventSecondaryTags)
  }, [selectedLiveEventSecondaryTags])

  return (
    <div className="LiveEventsSecondaryTaxonomySelector">
      <GRAccordion
        title={t('live-events:live_events_taxonomy')}
        expanded={liveEventsSecondaryTaxonomyOpen}
        onChange={() => setLiveEventsSecondaryTaxonomyOpen(!liveEventsSecondaryTaxonomyOpen)}
        extra={
          <AllOrNothingSelector
            someSelected={selectedTags.length > 0}
            deselectAll={onLiveEventsSecondaryTaxonomyCleared}
            selectAll={onLiveEventsSecondaryTaxonomySelectAll}
            disabled={isLoading || !liveEventsSecondaryTaxonomyOpen}
          />
        }
      >
        {!isLoading && selectableLiveEventSecondaryTagGroups?.length && selectableLiveEventSecondaryTags?.length && (
          <Masonry columns={4} spacing="1">
            {selectableLiveEventSecondaryTagGroups.map((group) => (
              <Card elevation={0} key={group.id} className="groupCard">
                <CardContent className="header">
                  <FormControlLabel
                    control={<Checkbox size="small" onChange={handleSelectGroup(group.id)} checked={group.checked} indeterminate={group.indeterminate} />}
                    label={group.name}
                    className="tagGroup"
                  />
                </CardContent>
                <Divider sx={{ marginBottom: '5px', marginTop: 0 }} variant="fullWidth" light />
                <CardContent className="content">
                  <Box sx={{ display: 'flex', flexDirection: 'column', ml: 0 }}>
                    {group.tags.map((tag) => {
                      const tagId = tag.id ?? ''
                      const isTagDisabled = applicability ? applicability[tagId] === false : false
                      const label = (
                        <FormControlLabel
                          className="tag"
                          key={tag.id}
                          control={<Checkbox size="small" onChange={handleSelectTag(group.id, tag.id || '')} checked={tag.checked} disabled={isTagDisabled} />}
                          label={tag.name || ''}
                        />
                      )
                      return isTagDisabled ? (
                        <Tooltip key={tag.id} title={notApplicableTitle}>
                          <span>{label}</span>
                        </Tooltip>
                      ) : (
                        label
                      )
                    })}
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Masonry>
        )}

        {isLoading && <div>Loading...</div>}
      </GRAccordion>
    </div>
  )
}

export default LiveEventsSecondaryTaxonomySelector
